<template>
    <div v-if="canShowAlert" ref="alertContainer" class="no-seating-plan-alert">
        <div class="no-seating-plan-alert__content">
            <div class="no-seating-plan-alert__left">
                <feather type="alert-triangle" class="no-seating-plan-alert__icon" />
                <strong class="no-seating-plan-alert__title">{{ $tl("labels.notifications.booking.seatingPlanRoomDisabled.bannerTitle") }}</strong>
                <span class="no-seating-plan-alert__separator">-</span>
                <span class="no-seating-plan-alert__subtitle">
                    {{ $tl("labels.notifications.booking.seatingPlanRoomDisabled.detectedErrors", undefined, { count: errorCount }) }}
                </span>
            </div>

            <div class="no-seating-plan-alert__actions">
                <button type="button" class="no-seating-plan-alert__details-btn" @click="toggleDetails">
                    <span>
                        {{ $tl(`labels.notifications.booking.seatingPlanRoomDisabled.${showDetails ? "hideDetails" : "showDetails"}`) }}
                    </span>
                    <span class="no-seating-plan-alert__badge">{{ affectedSeatingPlansCount }}</span>
                </button>
                <span class="no-seating-plan-alert__actions-separator"></span>
                <feather type="x" class="no-seating-plan-alert__close pointer" @click="markAsRead" />
            </div>
        </div>

        <div v-if="showDetails" class="no-seating-plan-alert__details">
            <strong class="no-seating-plan-alert__details-title">
                {{
                    $tl("labels.notifications.booking.seatingPlanRoomDisabled.affectedPlansAndRooms", undefined, {
                        count: affectedSeatingPlansCount,
                    })
                }}
            </strong>
            <ul class="no-seating-plan-alert__details-list">
                <li v-for="seatingPlan in seatingPlans" :key="`${seatingPlan.restaurantId}-${seatingPlan.seatingPlanId}`">
                    <router-link
                        :to="{
                            name: 'booking.restaurants.settings.seatingPlan',
                            params: {
                                restaurant_id: seatingPlan.restaurantId,
                                seating_plan_id: seatingPlan.seatingPlanId,
                            },
                        }">
                        {{ seatingPlan.seatingPlanName }}
                    </router-link>
                    <small class="text-muted"> - {{ formatRoomNames(seatingPlan.roomNames) }}</small>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        unreadSeatingPlanNotifications: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            canDisplayAlert: true,
            showDetails: false,
            resizeObserver: null,
            isHiddenOnScroll: false,
            scrollContainer: null,
            hideProgressThreshold: 0.06,
            showProgressThreshold: 0.015,
        };
    },
    computed: {
        notifications() {
            return Array.isArray(this.unreadSeatingPlanNotifications) ? this.unreadSeatingPlanNotifications : [];
        },
        canShowAlert() {
            return this.canDisplayAlert && this.notifications.length > 0 && !this.isHiddenOnScroll;
        },
        errorCount() {
            return this.notifications.length;
        },
        affectedSeatingPlansCount() {
            return this.seatingPlans.length;
        },
        seatingPlans() {
            const seatingPlanByIds = {};

            this.notifications.forEach((notification) => {
                if (!notification || !notification.data) {
                    return;
                }

                const seatingPlanId = notification.data.seatingPlanId;
                const restaurantId = notification.restaurant_id;
                const key = `${restaurantId}-${seatingPlanId}`;
                const roomNames = Array.isArray(notification.data.roomNames) ? notification.data.roomNames : [];

                if (!seatingPlanByIds[key]) {
                    seatingPlanByIds[key] = {
                        seatingPlanId,
                        seatingPlanName: notification.data.seatingPlanName,
                        restaurantId,
                        roomNames: [...new Set(roomNames)],
                        count: 1,
                    };
                    return;
                }

                seatingPlanByIds[key].count += 1;
                seatingPlanByIds[key].roomNames = [...new Set(seatingPlanByIds[key].roomNames.concat(roomNames))];
            });

            return Object.values(seatingPlanByIds);
        },
    },
    methods: {
        getScrollMetrics() {
            const windowScrollTop = window.scrollY || 0;
            const windowMax = Math.max(0, (document.documentElement.scrollHeight || 0) - window.innerHeight);

            if (!this.scrollContainer) {
                return {
                    scrollTop: windowScrollTop,
                    maxScrollTop: windowMax,
                };
            }

            const containerScrollTop = this.scrollContainer.scrollTop || 0;
            const containerMax = Math.max(0, (this.scrollContainer.scrollHeight || 0) - this.scrollContainer.clientHeight);

            if (containerMax > 0) {
                return {
                    scrollTop: containerScrollTop,
                    maxScrollTop: containerMax,
                };
            }

            return {
                scrollTop: windowScrollTop,
                maxScrollTop: windowMax,
            };
        },
        syncLayoutOffset() {
            if (!this.canShowAlert || typeof document === "undefined") {
                this.setLayoutOffset(0);
                return;
            }

            const alertContainer = this.$refs.alertContainer;
            if (!alertContainer) {
                this.setLayoutOffset(0);
                return;
            }

            const styles = window.getComputedStyle(alertContainer);
            const marginTop = Number.parseFloat(styles.marginTop) || 0;
            const marginBottom = Number.parseFloat(styles.marginBottom) || 0;
            const height = alertContainer.getBoundingClientRect().height + marginTop + marginBottom;

            this.setLayoutOffset(Math.ceil(height));
        },
        setLayoutOffset(value) {
            if (typeof document === "undefined") {
                return;
            }

            document.documentElement.style.setProperty("--no-seating-plan-alert-height", `${Math.max(0, value)}px`);
        },
        observeAlertHeight() {
            this.disconnectResizeObserver();

            if (!this.canShowAlert || typeof ResizeObserver === "undefined") {
                return;
            }

            const alertContainer = this.$refs.alertContainer;
            if (!alertContainer) {
                return;
            }

            this.resizeObserver = new ResizeObserver(() => {
                this.syncLayoutOffset();
            });

            this.resizeObserver.observe(alertContainer);
        },
        updateScrollVisibility() {
            if (typeof window === "undefined") {
                return;
            }

            const { scrollTop, maxScrollTop } = this.getScrollMetrics();

            if (maxScrollTop <= 0) {
                this.isHiddenOnScroll = false;
                return;
            }

            const progress = scrollTop / maxScrollTop;

            if (!this.isHiddenOnScroll && progress >= this.hideProgressThreshold) {
                this.isHiddenOnScroll = true;
                return;
            }

            if (this.isHiddenOnScroll && progress <= this.showProgressThreshold) {
                this.isHiddenOnScroll = false;
            }
        },
        registerScrollListeners() {
            if (typeof window === "undefined") {
                return;
            }

            window.addEventListener("scroll", this.updateScrollVisibility, { passive: true });
            this.scrollContainer = document.querySelector('[role="main"]');

            if (this.scrollContainer) {
                this.scrollContainer.addEventListener("scroll", this.updateScrollVisibility, { passive: true });
            }
        },
        unregisterScrollListeners() {
            if (typeof window === "undefined") {
                return;
            }

            window.removeEventListener("scroll", this.updateScrollVisibility);

            if (this.scrollContainer) {
                this.scrollContainer.removeEventListener("scroll", this.updateScrollVisibility);
                this.scrollContainer = null;
            }
        },
        disconnectResizeObserver() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }
        },
        toggleDetails() {
            this.showDetails = !this.showDetails;
        },
        formatRoomNames(roomNames) {
            if (Array.isArray(roomNames) && roomNames.length > 0) {
                return roomNames.join(", ");
            }

            return this.$tl("labels.notifications.booking.seatingPlanRoomDisabled.allRoomsUnavailable");
        },
        markAsRead() {
            const postPromises = this.notifications.map((notification) => {
                const url = `/api/restaurants/${notification.restaurant_id}/notifications/booking/${notification.id}/read`;
                return this.httpPost(url, {}, { handleResolve: false, handleReject: false });
            });

            Promise.allSettled(postPromises).then(() => {
                this.canDisplayAlert = false;
                this.showDetails = false;
                this.disconnectResizeObserver();
                this.syncLayoutOffset();
            });
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.registerScrollListeners();
            this.updateScrollVisibility();
            this.observeAlertHeight();
            this.syncLayoutOffset();
        });
    },
    beforeDestroy() {
        this.disconnectResizeObserver();
        this.unregisterScrollListeners();
        this.setLayoutOffset(0);
    },
    watch: {
        canShowAlert() {
            this.$nextTick(() => {
                this.observeAlertHeight();
                this.syncLayoutOffset();
            });
        },
        "$route.fullPath"() {
            this.$nextTick(() => {
                this.isHiddenOnScroll = false;
                this.unregisterScrollListeners();
                this.registerScrollListeners();
                this.updateScrollVisibility();
            });
        },
        showDetails() {
            this.$nextTick(() => {
                this.syncLayoutOffset();
            });
        },
        notifications: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.observeAlertHeight();
                    this.syncLayoutOffset();
                });
            },
        },
    },
};
</script>

<style scoped>
.no-seating-plan-alert {
    position: sticky;
    top: 60px;
    z-index: 20;
    margin: 12px 16px 0;
    border: 1px solid #efc8ce;
    border-radius: 8px;
    background-color: #fbecee;
    box-shadow: 0 1px 2px rgba(108, 36, 41, 0.06);
    color: #5e6577;
}

.no-seating-plan-alert__content {
    padding: 8px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
}

.no-seating-plan-alert__left {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
}

.no-seating-plan-alert__icon {
    color: #f3a536;
    width: 16px;
    height: 16px;
}

.no-seating-plan-alert__title {
    color: #b14a50;
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1;
}

.no-seating-plan-alert__subtitle {
    color: #626b7f;
    font-size: 0.75rem;
    line-height: 1;
}

.no-seating-plan-alert__separator {
    color: #8d93a3;
}

.no-seating-plan-alert__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.no-seating-plan-alert__details-btn {
    border: 0;
    background: transparent;
    color: #556b8d;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    line-height: 1;
    font-size: 0.78rem;
}

.no-seating-plan-alert__details-btn:hover {
    text-decoration: underline;
}

.no-seating-plan-alert__badge {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background-color: #f0d5d9;
    color: #a2454c;
    font-size: 0.52rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.no-seating-plan-alert__actions-separator {
    width: 1px;
    height: 14px;
    background-color: #d9cacf;
}

.no-seating-plan-alert__close {
    color: #a6979d;
    width: 16px;
    height: 16px;
}

.no-seating-plan-alert__details {
    border-top: 1px solid #efd6da;
    padding: 8px 14px 12px;
}

.no-seating-plan-alert__details-title {
    color: #5f6578;
    font-size: 0.86rem;
}

.no-seating-plan-alert__details-list {
    margin: 6px 0 0;
    padding-left: 16px;
    max-height: 180px;
    overflow-y: auto;
}

.no-seating-plan-alert__details-list li {
    margin-bottom: 4px;
}

@media (max-width: 767px) {
    .no-seating-plan-alert__content {
        align-items: flex-start;
        flex-direction: column;
    }

    .no-seating-plan-alert__actions {
        width: 100%;
        justify-content: flex-end;
    }
}
</style>
